import type {OpenChatParams} from "../types";

/**
 * Открытая переписка в записи истории `/app/chats` (issue #1132).
 *
 * Переписка — состояние страницы «Сообщения» (`activeSession`), а не отдельный
 * адрес. Поэтому любой уход из диалога вперёд («Предложить встречу» → «Куда
 * сходить», ссылка на место в сообщении) и затем «назад» — браузера, Android,
 * свайпа или стрелки на странице — раньше возвращал к списку чатов: запись
 * `/app/chats` о диалоге ничего не помнила.
 *
 * Пока диалог открыт, его параметры лежат в `history.state` текущей записи
 * (ключ `openChat` внутри `usr` — туда React Router кладёт location.state).
 * Возврат на запись или перезагрузка страницы видят его в `location.state`,
 * и «Сообщения» открываются сразу в этом диалоге.
 *
 * Пишем напрямую через `history.replaceState`, а не через `navigate(replace)`:
 * это не навигация — роутер не должен перерисовывать вкладку, сбрасывать
 * прокрутку или прерывать переход, который как раз уводит с этой страницы.
 */
export const OPEN_CHAT_STATE_KEY = "openChat";

export type OpenChatSnapshot = OpenChatParams & {conversationId: string};

/** Открытая переписка из location.state записи (или null). */
export function readOpenChat(state: unknown): OpenChatSnapshot | null {
    if (!state || typeof state !== "object") return null;
    const value = (state as Record<string, unknown>)[OPEN_CHAT_STATE_KEY];
    if (!value || typeof value !== "object") return null;
    const snapshot = value as Partial<OpenChatSnapshot>;
    if (typeof snapshot.conversationId !== "string" || !snapshot.conversationId) return null;
    if (typeof snapshot.userName !== "string") return null;
    return {
        conversationId: snapshot.conversationId,
        userName: snapshot.userName,
        userAvatar: typeof snapshot.userAvatar === "string" ? snapshot.userAvatar : "",
        ...(typeof snapshot.peerUserId === "string" ? {peerUserId: snapshot.peerUserId} : {}),
        ...(typeof snapshot.peerLastSeenAt === "string" ? {peerLastSeenAt: snapshot.peerLastSeenAt} : {}),
    };
}

/**
 * Записать (или снять, если null) открытую переписку в текущую запись истории.
 * Черновик (`prefilledMessage`) не сохраняем: при возврате он ушёл бы в поле
 * ввода второй раз.
 */
export function rememberOpenChat(session: OpenChatSnapshot | null): void {
    if (typeof window === "undefined") return;
    const current = (window.history.state ?? {}) as Record<string, unknown>;
    const usr = current.usr && typeof current.usr === "object" ? {...(current.usr as Record<string, unknown>)} : {};
    const stored = readOpenChat(usr);
    if (session) {
        if (stored?.conversationId === session.conversationId
            && stored.userName === session.userName
            && stored.userAvatar === session.userAvatar) return;
        usr[OPEN_CHAT_STATE_KEY] = {
            conversationId: session.conversationId,
            userName: session.userName,
            userAvatar: session.userAvatar,
            ...(session.peerUserId ? {peerUserId: session.peerUserId} : {}),
            ...(session.peerLastSeenAt ? {peerLastSeenAt: session.peerLastSeenAt} : {}),
        };
    } else {
        if (!(OPEN_CHAT_STATE_KEY in usr)) return;
        delete usr[OPEN_CHAT_STATE_KEY];
    }
    window.history.replaceState({...current, usr: Object.keys(usr).length ? usr : null}, "");
}
